import { FC, useState, useEffect, useRef } from 'react';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../contexts/TourContext';
import { useTranslatedSteps } from './stepsTour';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tab,
  Tabs,
  Tooltip,
  Button,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from '../contexts/TranslationContext';
import { onboarding, chatbox } from '../services/api';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';
import { parseMarkdown, handleMarkdownButtonClick } from '../utils/parseMarkdown';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface PersistedMessage extends Omit<Message, 'timestamp'> {
  timestamp: string;
}

interface PendingPollingState {
  correlationId: string;
  typingMessageId: string;
  attempts: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chatbox-tabpanel-${index}`}
      aria-labelledby={`chatbox-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const ChatboxContent: FC = () => {
  const { t, tArray } = useTranslation();
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copyTooltip, setCopyTooltip] = useState(t('chatbox.message.copy'));
  const [chatboxId, setChatboxId] = useState<string>('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const isInitialMessagesScrollRef = useRef(true);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const CHATBOX_MESSAGES_STORAGE_KEY = 'aida_chatbox_messages_v1';
  const CHATBOX_PENDING_STORAGE_KEY = 'aida_chatbox_pending_v1';
  const CHATBOX_FAQ_QUESTIONS_STORAGE_KEY = 'aida_chatbox_faq_questions_v1';
  const CHATBOX_TIME_OPEN_STORAGE_KEY = 'aida_chatbox_time_open_v1';
  const CHATBOX_PROCESSING_STORAGE_KEY = 'aida_chatbox_processing_v1';

  const readTimeOpenFromStorage = (): { enabled: boolean; seconds: number } => {
    if (typeof window === 'undefined') {
      return { enabled: false, seconds: 1 };
    }
    try {
      const raw = localStorage.getItem(CHATBOX_TIME_OPEN_STORAGE_KEY);
      if (!raw) return { enabled: false, seconds: 1 };
      const parsed = JSON.parse(raw) as { enabled?: unknown; seconds?: unknown };
      const enabled = parsed.enabled === true;
      const seconds =
        typeof parsed.seconds === 'number' && parsed.seconds >= 1
          ? Math.floor(parsed.seconds)
          : 1;
      return { enabled, seconds };
    } catch {
      return { enabled: false, seconds: 1 };
    }
  };

  // Chatbox settings state
  const [position, setPosition] = useState('right'); // 'right' or 'left'
  const [defaultState, setDefaultState] = useState('closed'); // 'closed' or 'open'
  const [timeOpenEnabled, setTimeOpenEnabled] = useState(
    () => readTimeOpenFromStorage().enabled,
  );
  const [timeOpenSeconds, setTimeOpenSeconds] = useState(
    () => readTimeOpenFromStorage().seconds,
  );
  const [processingEnabled, setProcessingEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const raw = localStorage.getItem(CHATBOX_PROCESSING_STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { enabled?: unknown };
      return parsed.enabled === true;
    } catch {
      return false;
    }
  });
  const [faqQuestions, setFaqQuestions] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(CHATBOX_FAQ_QUESTIONS_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item): item is string => typeof item === 'string');
    } catch {
      return [];
    }
  });
  const normalizedFaqQuestions = faqQuestions
    .map((question) => question.trim())
    .filter((question) => question.length > 0)
    .slice(0, 3);
  const faqQuestionsPayload = JSON.stringify(normalizedFaqQuestions).replace(/'/g, '&#39;');
  const timeOpenValue = timeOpenEnabled ? String(timeOpenSeconds) : '-1';
  const scriptTagCode = `<script src="https://cdn.aidasales.ir/chatbox/aida-chatbot.min.fa.js" data-aida-api-key="${chatboxId}" data-position-chatbox="${position}" data-initial-state="${defaultState}" data-timeOpen="${timeOpenValue}" data-processing="${processingEnabled}" data-questions='${faqQuestionsPayload}'></script>`;

  const handleTimeOpenSecondsChange = (rawValue: string) => {
    if (rawValue === '') {
      setTimeOpenSeconds(1);
      return;
    }
    const parsed = Number.parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return;
    setTimeOpenSeconds(Math.max(1, parsed));
  };

  const handleFaqQuestionChange = (index: number, value: string) => {
    setFaqQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions[index] = value;
      return updatedQuestions;
    });
  };

  const handleAddFaqQuestion = () => {
    setFaqQuestions((prevQuestions) => {
      if (prevQuestions.length >= 3) return prevQuestions;
      return [...prevQuestions, ''];
    });
  };

  const handleRemoveFaqQuestion = (index: number) => {
    setFaqQuestions((prevQuestions) => prevQuestions.filter((_, currentIndex) => currentIndex !== index));
  };

  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  useEffect(() => {
    fetchChannelInfo();
    // اسکرول به بالای صفحه در هنگام لود کامپوننت
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    try {
      const storedMessages = localStorage.getItem(CHATBOX_MESSAGES_STORAGE_KEY);
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages) as PersistedMessage[];
        setMessages(
          parsedMessages.map((message) => ({
            ...message,
            timestamp: new Date(message.timestamp),
          }))
        );
      }
    } catch (error) {
      console.error('Error loading chat history from localStorage:', error);
    } finally {
      setIsHistoryLoaded(true);
    }
  }, []);

  useEffect(() => {
    // اسکرول به بالای صفحه بعد از هر تغییر در activeTab
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  useEffect(() => {
    if (!isHistoryLoaded) return;

    if (isInitialMessagesScrollRef.current) {
      isInitialMessagesScrollRef.current = false;
      if (messages.length > 0) {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
      return;
    }

    scrollToBottom();
  }, [messages, isHistoryLoaded]);

  useEffect(() => {
    if (!isHistoryLoaded) return;
    try {
      const serializableMessages: PersistedMessage[] = messages.map((message) => ({
        ...message,
        timestamp: message.timestamp.toISOString(),
      }));
      localStorage.setItem(CHATBOX_MESSAGES_STORAGE_KEY, JSON.stringify(serializableMessages));
    } catch (error) {
      console.error('Error saving chat history to localStorage:', error);
    }
  }, [messages, isHistoryLoaded]);

  useEffect(() => {
    try {
      localStorage.setItem(CHATBOX_FAQ_QUESTIONS_STORAGE_KEY, JSON.stringify(faqQuestions));
    } catch (error) {
      console.error('Error saving FAQ questions to localStorage:', error);
    }
  }, [faqQuestions]);

  useEffect(() => {
    try {
      localStorage.setItem(
        CHATBOX_TIME_OPEN_STORAGE_KEY,
        JSON.stringify({ enabled: timeOpenEnabled, seconds: timeOpenSeconds }),
      );
    } catch (error) {
      console.error('Error saving time open settings to localStorage:', error);
    }
  }, [timeOpenEnabled, timeOpenSeconds]);

  useEffect(() => {
    try {
      localStorage.setItem(
        CHATBOX_PROCESSING_STORAGE_KEY,
        JSON.stringify({ enabled: processingEnabled }),
      );
    } catch (error) {
      console.error('Error saving processing settings to localStorage:', error);
    }
  }, [processingEnabled]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const clearPendingPollingState = () => {
    localStorage.removeItem(CHATBOX_PENDING_STORAGE_KEY);
  };

  const savePendingPollingState = (pendingState: PendingPollingState) => {
    localStorage.setItem(CHATBOX_PENDING_STORAGE_KEY, JSON.stringify(pendingState));
  };

  const handlePollingTimeout = (typingMessageId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    clearPendingPollingState();
    setMessages((prev) => {
      const filtered = prev.filter((msg) => msg.id !== typingMessageId);
      const timeoutMessage: Message = {
        id: Date.now().toString(),
        content: 'جوابی دریافت نشد',
        sender: 'ai',
        timestamp: new Date(),
      };
      return [...filtered, timeoutMessage];
    });
    setLoading(false);
  };

  const handlePollingError = (typingMessageId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    clearPendingPollingState();
    setMessages((prev) => {
      const filtered = prev.filter((msg) => msg.id !== typingMessageId);
      const errorMessage: Message = {
        id: Date.now().toString(),
        content: t('dashboard.chatbox.errorMessage') || 'خطا در دریافت پاسخ',
        sender: 'ai',
        timestamp: new Date(),
      };
      return [...filtered, errorMessage];
    });
    setLoading(false);
  };

  const startPollingForCorrelationId = async (
    correlationId: string,
    typingMessageId: string,
    initialAttempts = 0
  ) => {
    let pollAttempts = initialAttempts;
    setLoading(true);
    savePendingPollingState({ correlationId, typingMessageId, attempts: pollAttempts });

    const pollResult = async () => {
      const { data: resultData } = await chatbox.getResult(correlationId);
      if (resultData.status === 'pending') {
        return false;
      }

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      clearPendingPollingState();
      setMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== typingMessageId);
        const aiMessage: Message = {
          id: Date.now().toString(),
          content: resultData.response || resultData.message || 'پاسخ دریافت شد',
          sender: 'ai',
          timestamp: new Date(),
        };
        return [...filtered, aiMessage];
      });
      setLoading(false);
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
      return true;
    };

    try {
      if (pollAttempts >= 20) {
        handlePollingTimeout(typingMessageId);
        return;
      }

      pollAttempts += 1;
      savePendingPollingState({ correlationId, typingMessageId, attempts: pollAttempts });
      const gotResult = await pollResult();
      if (gotResult) return;

      pollingIntervalRef.current = setInterval(async () => {
        try {
          if (pollAttempts >= 20) {
            handlePollingTimeout(typingMessageId);
            return;
          }
          pollAttempts += 1;
          savePendingPollingState({ correlationId, typingMessageId, attempts: pollAttempts });
          const isDone = await pollResult();
          if (isDone && pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } catch (pollError) {
          console.error('Error polling result:', pollError);
          handlePollingError(typingMessageId);
        }
      }, 5000);
    } catch (pollError) {
      console.error('Error polling result:', pollError);
      handlePollingError(typingMessageId);
    }
  };

  useEffect(() => {
    if (!isHistoryLoaded) return;
    try {
      const pendingStateRaw = localStorage.getItem(CHATBOX_PENDING_STORAGE_KEY);
      if (!pendingStateRaw) return;
      const pendingState = JSON.parse(pendingStateRaw) as PendingPollingState;
      if (!pendingState?.correlationId || !pendingState?.typingMessageId) {
        clearPendingPollingState();
        return;
      }
      setMessages((prev) => {
        const hasTypingMessage = prev.some((msg) => msg.id === pendingState.typingMessageId);
        if (hasTypingMessage) return prev;
        const typingMessage: Message = {
          id: pendingState.typingMessageId,
          content: t('dashboard.chatbox.isTyping') || '...',
          sender: 'ai',
          timestamp: new Date(),
        };
        return [...prev, typingMessage];
      });
      startPollingForCorrelationId(
        pendingState.correlationId,
        pendingState.typingMessageId,
        pendingState.attempts || 0
      );
    } catch (error) {
      console.error('Error restoring pending polling state:', error);
      clearPendingPollingState();
    }
  }, [isHistoryLoaded]);

  const handleClearChatHistory = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    clearPendingPollingState();
    localStorage.removeItem(CHATBOX_MESSAGES_STORAGE_KEY);
    setMessages([]);
    setLoading(false);
  };

  const fetchChannelInfo = async () => {
    try {
      const { data } = await onboarding.getWebsiteChatbox();
      setChatboxId(data.website_chatbox_id || '');
    } catch (error) {
      console.error('Error fetching channel info:', error);
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleSendMessage = async () => {
    const userMessageText = newMessage.trim();
    if (!userMessageText) {
      enqueueSnackbar(t('chatbox.message.emptyMessage'), { variant: 'warning', autoHideDuration: 1000 });
      return;
    }
    if (loading) {
      enqueueSnackbar(t('chatbox.message.waitForResponse'), { variant: 'warning', autoHideDuration: 1000 });
      return;
    }
    if (!chatboxId) {
      enqueueSnackbar(t('chatbox.message.noChatboxId'), { variant: 'warning', autoHideDuration: 1000 });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: userMessageText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setNewMessage('');
    setLoading(true);

    const typingMessage: Message = {
      id: `typing-${Date.now()}`,
      content: t('dashboard.chatbox.isTyping') || '...',
      sender: 'ai',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, typingMessage]);

    try {
      const { data } = await chatbox.sendMessage(chatboxId, {
        message: userMessageText,
        thread_id: 'sandbox',
        username: 'sandbox_user',
      });
      const correlationId = data.correlation_id;

      if (!correlationId) {
        throw new Error('No correlation_id received');
      }
      await startPollingForCorrelationId(correlationId, typingMessage.id, 0);
    } catch (error: any) {
      console.error('Error sending message:', error);
      handlePollingError(typingMessage.id);
      if (error.response?.status === 404) {
        enqueueSnackbar(t('chatbox.message.websiteAddressNotConfigured'), {
          variant: 'error',
          autoHideDuration: 2000,
        });
      }
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ px: { xs: 0.5, sm: 1, md: 2 }, py: { xs: 0, md: 2 }, width: '100%', overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px', px: { xs: 0.5, md: 0 } }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="chatbox tabs"
          sx={{
            border: '1.5px solid black',
            borderBottom: 'none',
            borderTopRightRadius: { xs: '10px', sm: '14px', md: '16px' },
            borderTopLeftRadius: { xs: '10px', sm: '14px', md: '16px' },
            '& .MuiTabs-flexContainer': { justifyContent: 'center' },
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              backgroundColor: '#D9D9D980',
              color: '#000',
              fontWeight: 400,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              minWidth: { xs: '60px', sm: '80px', md: '120px' },
              flex: '1 1 0',
              textAlign: 'center',
              fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
              px: { xs: 0.75, sm: 1, md: 2 },
              py: { xs: 0.75, sm: 1, md: 1.25 },
              whiteSpace: 'nowrap',
            },
            '& .MuiTab-root:first-of-type': {
              borderTopLeftRadius: { xs: '9px', sm: '13px', md: '15px' },
              borderRight: '1.5px solid black',
            },
            '& .MuiTab-root:last-of-type': { borderTopRightRadius: { xs: '9px', sm: '13px', md: '15px' } },
            '& .MuiTab-root.Mui-selected': {
              backgroundColor: '#009688',
              color: '#fff',
              fontWeight: 700,
            },
          }}
        >
          <Tab label={t('chatbox.tabs.chatbox')} className='first-step' id="chatbox-tab-0" aria-controls="chatbox-tabpanel-0" />
          <Tab label={t('chatbox.tabs.chatbox_properties')} className='third-step' id="chatbox-tab-1" aria-controls="chatbox-tabpanel-1" />
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, border: '1.5px solid black', borderRadius: '20px', margin: { xs: '0 8px', md: '0 20px' } }}>
        <TabPanel value={activeTab} index={0}>
        <Grid container spacing={{ xs: 1, md: 3 }}>
          <Grid item xs={12} md={8}>
            <Box sx={{
              height: { xs: 'calc(100dvh - 220px)', sm: 'calc(100dvh - 200px)', md: '70vh' },
              minHeight: { xs: 360, md: 400 },
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              borderRight: { xs: 'none', md: '1.5px solid black' },
              borderBottom: { xs: '1.5px solid black', md: 'none' },
              borderRadius: { xs: '20px 20px 0px 0px', md: '20px 0px 0px 20px' },
            }}>
              <Tooltip title={t('dashboard.chatbox.clearHistory')}>
                <IconButton
                  onClick={handleClearChatHistory}
                  size="medium"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 10,
                    zIndex: 2,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                    },
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="#000" width="20px" viewBox="0 0 612.002 612.002">
                    <g>
                      <path d="M594.464,534.414H344.219L606.866,271.77c6.848-6.851,6.848-17.953,0-24.8L407.547,47.65 c-3.291-3.288-7.749-5.135-12.401-5.135c-4.65,0-9.11,1.847-12.398,5.135L5.138,425.262c-6.851,6.848-6.851,17.95,0,24.8 l114.29,114.287c3.288,3.291,7.749,5.138,12.398,5.138h462.638c9.684,0,17.536-7.852,17.536-17.536 C612,542.265,604.148,534.414,594.464,534.414z M395.145,84.851L569.664,259.37L363.27,465.763L188.753,291.245L395.145,84.851z M294.618,534.414H139.09L42.336,437.66l121.617-121.617l174.519,174.519L294.618,534.414z" />
                    </g>
                  </svg>
                </IconButton>
              </Tooltip>
              <Box ref={messagesContainerRef} sx={{ flexGrow: 1, overflow: 'auto', pt: { xs: 5, md: 2 }, pb: 1, px: { xs: 0.5, md: 0 } }}>
                <List sx={{ direction: 'rtl', py: 0 }}>
                  {messages.map((message) => (
                    <ListItem
                      key={message.id}
                      sx={{
                        justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                        mb: { xs: 1, md: 2 },
                        px: { xs: 0.5, md: 2 },
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: { xs: '95%', sm: '65%', md: '50%' },
                          width: 'max-content',
                          display: 'flex',
                          flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                          alignItems: 'flex-start',
                          p: { xs: 0.5, md: 1 },
                          borderRadius: '10px',
                        }}
                      >
                        {message.sender === 'ai' && (
                  <img 
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAP4AAAE5AgMAAADNYnV6AAADKGlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78i iglkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgNy4yLWMwMDAgNzkuMWI2NWE3OWI0LCAyMDIyLzA2LzEzLTIyOjAxOjAxICAgICAgICAiPiA8cmRmOlJERiB4bWxuczpypmY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtbG5zOnhtcE1NPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvbW0vIiB4bWxuczpzdFJlZj0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL3NUeXBlL1Jlc291cmNlUmVmIyIgeG1wOkNyZWF0b3JUb29sPSJBZG9iZSBQaG90b3Nob3AgMjMuNSAoTWFjaW50b3NoKSIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDpEM0FDRkEwNDRGNkMxMUYwQjRGM0ZGQzZGQUQwMjc1NCIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDozMjM3RDgwODRGNkYxMUYwQjRGM0ZGQzZGQUQwMjc1NCI+IDx4bXBNTTpEZXJpdmVkRnJvbSBz
              dFJlZjppbnN0YW5jZUlEPSJ4bXAuaWlkOkQzQUNGQTAyNEY2QzExRjBCNEYzRkZDNkZBRDAyNzU0IiBzdFJlZjpkb2N1bWVudElEPSJ4bXAuZGlkOkQzQUNGQTAzNEY2QzExRjBCNEYzRkZDNkZBRDAyNzU0Ii8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+wSK1rgAAABl0RVh0U29mdHdhcmUAQWRvYmUgSW1hZ2VSZWFkeXHJZTwAAAAJUExURSMfICEpKQqvn8uancoAAAADdFJOU/AD2OyXvisAAA1SSURBVHja1Zy9jiS3Ecf/tdgSoI3mgKEBOXLig7VPUQbkvA6oEixFigR4n2IewYkE30UTHfb4lA740c1usrtnVoKtSW53tvvXxWKxvsg++M7HHt+FjT8rdu4XAKD7AQIAQLgXoOn+DRF2AMAjABLcCVCQAY/w8Ri2AYArHglB+C6AIrieH0nYcBdA4C5nJiW/DwC4izEMLuEOgIHcYSSOoHwHQMHuZCRBgtEdADmxGxlpUB4qYQsACW7kpGw0VMIGwEiCGzsZG/nIEjYAynBXdjJ2uN4OEIa7Bicjh4+0uAUIVAESRlrcAMDI052/AUDDaBrGACNjd3FHUqHeDOAMkAzgGwHKVQJxuwugRQJxI7ebAUHZHb8RwGlkSWOAzADhLoC3AP+jA/DHlMDeKkFdTP8XErwR4HcC6G2AkAG4G+A0dyg3AzQ4sldODuVtAL4LICkyOdz4DpfGLqEA9D6AZgDdFRdSKCiAcBcgMSgF1ztiY44GZOTitwOc3JHT/Xfwu8I7Zp/bAc3t45JhBJhu/45OW4QBIN1PKUfToKwjQh+gAHAiN3IYSxB2A
              cBHAQaA3eCODAgO127t1QUAcHdHcAliLA43uAtwDFAuFHYJcmIYuZK7S2cQHYCVBym5sp6YtKTKHRE6AClDNbiR4kzCuWyztQhrwOwisENxYoRy2bqAXANm1wgcZ5we4UIr+hAwu8TA8giArE7gSgTd/EJwIgA83WZLW1gBmsrEAAgwF1xoG7B4Qq7+Z18ub1j9TiujTJ9/1G/CJmBRW7U+pTeGJaDhL31KNqYtgKGjALx7Nye0Y1gAZipIj3+nOOU5KC6lHeUCMP3RkDyIgMskWHIprZ4XgCqeZQdkAOuMsFJCC5j+VhyYAuRSCAr2RYhZAGi6P12FR2D2q2CphBZQAuj
              skWcI1wGl1dikGi1A6lizlCCFUlJJKGbUKKEFoAygShIE6fpsiYbQWkIDyGgtIURBLsgeIlsBuFVCC6DGqToQHFKcyGQFOgKkP1SnCrAbSbCiQIQ0hrkWWwcUSvOnhgclCcWbp6EJN7lCA8BcgDSVysJVKYKQxiBhA5Av1jRkBFCdFgOSpgcAo9yAm+435JuKYbM7moxtDlCuAmieNKW0ksrvgLs0ae8cIFycqpVsQljSwyV9o2kORoBQPF7NRhCKM5T0L8i9SdnmAJS4VBMBhQGPyOYBSiI0WecMYGWdtGuptmSTHsA5De8DpMkjDEEAKoFJAXZFyhs6AE1TNK0ll5RrVttSwB3BMMv9ZwAJaQR1LRlYASDMrJtc2s5aC1Aqfi9PuiTfXAcFhNRg7QGQFwrqADiHpkmtBkoNVnQA5I4w5SAGuEJSQNLipARBWNllDTDKyaBPUQQMIUjyy9mlkdK8QzoBlF1pEgAgN5wJJOnH7JgV59ze6wCEy+8GwF0gTKSUEt8UGw0sYdYhnQDJQOvCT+rAmWBQACkD50SV0AO4Y1qMCMmOlOHJHku8MpxIgq0B8JIS5/sNpKQsLlRWs6SBkU6d7gmQqgOeopAgCEuQkDN1zstEAWVfAYxc2RDq/QoyhFQrsJdqIYXa82RJE4BdgtIshsE1t1Oliu8KdsGZ1gBllyBcnpTtyMiVc8iTPAhXMFVTnADB4QhWxg9yQ9DkQEt0TJHJQJMpVoAEJ0N6TFrELlQ8cLLvHJnIQegA3EjJitXmZYXgjpoAp8gEF0i15QqAGwsLgpesVFKpNXPWybEgaA9ArowzuPhvd5RSK9R4JQjuwopp06UAjFzPUEwrXxFSJpEC1pQiKVkfICcC+dz3pFzGaKpUJEVXoC6GCmAXeUTQWXpUUh5MxZKl6Arw
              EqDskBPNHBLVzF7CTARylyAdQHBAeOaQQq2/lKdSxhBcWcBGLUCCA1N9qJjlgzZPPMBupD2AAVSemRK1Wpvk1RlyQWBQkC8BbgCXkgvJL3BTBICKOLABICjN07FF8MgigF3OawBcgfyonGnOWh711jSGHoBcUbZWi/cKi2pTsqdx5T6AiuOgeb45SZ+VAjdCdUklkJArWKkkAa0ARfokggSjqTE1ASTt7Vp1qevKKE2SskNWAHZBQKhJ6aJjk6UXSlocABz1yat2i0zGZOSCLsBoGgB6G7h5atEBKLuQllzGOi2nIkLa/+kDhMtzOv2iUnRy9mlLQHCwBEGT6C5E4KxGZesDcquj2/GaxRU3GgE0RT+MuqeUTQreBZwpB3cebsWnuJIAtAY8JkOijSZpGoME6UlwSun81l56rjqVBcVWGwB3LWAxCIUrS068KkCCQxC6HcOFHg3BSCAoRVBBQ9A6gf4gggs7BHi6VqW6A3hIrYqwDTDAlRwK+iG+JqV5Pa7Ctj2AXDkaXE6glxgvSSXuwMMrgCB7B3NyEREE4BhjTCWQK55+iZe2abclgpICX316ifFCrnAXxBivwBEB3AEjA76KMcZXuMINz/FzfAUdEcBdwTgDzzHG+AVB4YoEw7wcXcB0ZuECCcA3McYYL0Hh8pQB1HThVm09mtGzBPHKCs8/z25Z7QbIrJ/oDpDgfYwxxldWOGIGNJGsbVfRPFQqIPmmV1LYQwG0w24A6z5puimSwp5ekj6wZ8fN9kMGQKFPsQBwEGDAwwzwPgP4OEEq4KLQ5wKwwwSdAyQDrpyrRV3tDK2/GQBc05pcdJK9883TDBAnwOxZWwAFRoBFL7oFPADV/XQArzRqhhfA0wUoIj4PAbN+/PLzfKk9ctkCyAgQL1WuOwHTRHYBDzuAh9eHS
              y3R4hoQN2Y9A55WgC8XhXw+CHi6Pg0keHkj4GOR4JdtwPPleVoLX6blLL+Wn8NhQPFiSYI/5yFcwnLttYCI1wnweSbBx6LQv44WbwcQY4zxc4ykkD9lmPAIoBnwl/UQSCHZpX2+7AD+Nm3bWlqMLzGSQr9ZSGAbHqk84+uZBBVw/Xq5HHkFKHr+Kk6BRb/NSrzScoMrjL7QHEsS4H2RoJdgaX+7u9zzylNg6QP64aqu5ldWVIW8UjcG9bIO+XN1pAqrAPSOQcgbAf0DqvihAILC6MvCIfgwzFfAj3PAw8smoJuplVsuQeHVrg/nB9MtF1c4fr4VMK2l1DKsuMMA+jQHyM8vtwKm1eyKyayufDRHeppsT+Hyzad7Af9JGxbvb5WgphfXBJjW5vievzeA8sif2BVuW6tpuRmb7Oh5mjeF28OPu4B2wx3ftgD6fs+W22rOigSfkUvhn/cAWADeTzco3PH9ji0v6g/Fp2nMCncJL5sAA5oTXVIW4/XrBKhO+sKDAXCzW16KhXjhDOBNgADtfj3wr49VYoW7UQH0/XJtE9ZE+YdP1QemyvVlCEjnmNqzJUQfP9bLE6CUDOg9nxZnS5Tpl4+58C2dpaeUL1zRjwuyOCKAX39+ifGnqQGheQzX9TzS+gSPBODXGK9le3eyzS/XdRiiVWuxHpeb91AE8VOM8XLqARZtDUMO99Q0bB9ijBecV9OAdWuRXE9TBlZ7KIMDeMgt1mZ7W4Pjsd2k0ZyRoGcHi2/ao4rTYSuAOocQZRXfEabdyvkWyQcK7utzlLJqDMH7gEFG8yF0vCM2TgPRbkzgTYDvhngJ7aldXet4JzXYASjvVf6bR2lGeWH7d+PNY2U7KuAdgO+kKeloyRZAeE8F7fsk67N5tKeCrRNRu0oQ9taSe8cLw44K9gBCOy
              pwbAM23/Ki4ie3Lt8YQ9p92APIRm88+MKOeoCNc+C0Xi69q8ftffalHQ2O2o4SlbBe8T3AqD1ctiz3B9zvycmRE5K18ztO9ZQOqLxXLSLtYCyXax/Q2acQUArIO8dMp4AUlpnKP+tB7CNWgzYmAgg/IV5p96ht28sOTab1FF9pNcW6kR+nc+8fTrmVgEUDfnfxLs/IJgDfsPq9aQfZQ4yvuMl9OPw7AITaeHld28cOIO+KVwD7/xhwvQ1Q/ELekX+K8d/hFkDd+Ms7uQ/d6nr7FYxmYeBGQJ2/ejTx+fXg6wd1Z7AqMe1UdjdDhwBgPgt524CPA7QFaNoN9eMAkIRSsks5VuLHAYagwT/kbNVdT+OqdmRDbbEabnuTxrB4hwsbyhp9a4tTvLjlhah8zrcJy3LDuzwpD5jdMR3CPQbIecBs/zAMo34/uHIbY3Npd/htIpvOTM/DquEoQGkeXN5Nx/CPvo4ky4Y0bWTB3RwpeEsIG1lw922i2S8nrI4l7QLG+boc8wcb/9/DMY/UtTgbKaH3PtOo3uxWlZ1sncbSyxG3LjxOEvVIaOvXruhk+qOSx8cAP5Db9+t3677Hc0PZVwKtHADwFkD332vr67B0QtYDXNfOWzq8GzB7HWAPMJyE0QiPdTD0rYAp+9e9VHc0CXWjcBcwqj9ooKNjfSTFcBqWABrm7XwIMGqFTWNYKmkJGP6vJ/UU8TZAN4/V9a5YAsJG2ngEMOrFTZ1d2wEMM28eqPkgYMpWdgDYD2rbpS/th9VNwLClOlOubAJ4u/zqTNRBAB0EjAzRwtDWFoCRHY0fcgzgvyXAtgDyZoAfANAbAf67AvBWAB0B4PcE4NBnDLC3At4swT0f/S9sw79OxPul9AAAAABJRU5ErkJggg==" 
                  className='svg_bot_AIDACHATBOX'
                  alt="Bot"
                  style={{ width: 32, height: 38, flexShrink: 0, alignSelf: 'flex-end', marginBottom: 4 }}
                />
                        )}
                        <Paper
                          dir="auto"
                          sx={{
                            px: 2,
                            flex: 1,
                            minWidth: 0,
                            ml: message.sender === 'user' ? 0 : 1,
                            mr: message.sender === 'user' ? 0 : 1,
                            backgroundColor: message.sender === 'user' ? 'grey.200' : '#009688',
                            color: message.sender === 'user' ? 'text.primary' : 'white',
                          }}
                        >
                          <ListItemText
                            primary={
                              message.sender === 'ai' ? (
                                <Typography 
                                  component="div"
                                  dir="auto"
                                  onClick={handleMarkdownButtonClick}
                                  sx={{ 
                                    color: 'white',
                                    unicodeBidi: 'plaintext',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    '& a': {
                                      color: '#E0F2F1',
                                      textDecoration: 'underline',
                                    }
                                  }}
                                  dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content, 'chatbox') }}
                                />
                              ) : (
                                <Typography
                                  component="div"
                                  dir="auto"
                                  sx={{
                                    unicodeBidi: 'plaintext',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                  }}
                                >
                                  {message.content}
                                </Typography>
                              )
                            }
                            secondary={message.timestamp.toLocaleTimeString()}
                            secondaryTypographyProps={{
                              color: message.sender === 'user' ? 'text.secondary' : 'rgba(255,255,255,0.8)',
                            }}
                          />
                        </Paper>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Divider />
              <Box sx={{ p: { xs: 1, md: 2 }, display: 'flex', alignItems: 'flex-end', gap: 0.5, minWidth: 0 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('chatbox.message.placeholder')}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  multiline
                  maxRows={4}
                  inputRef={textFieldRef}
                  size="small"
                  sx={{ minWidth: 0 }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={loading || !newMessage.trim()}
                  sx={{ flexShrink: 0 }}
                >
                  <SendIcon sx={{ transform: 'rotate(180deg)' }} />
                </IconButton>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ marginTop: { xs: 0, md: '10px' }, mx: { xs: 1, md: 0 } }}>
              <CardHeader 
                title={<Typography variant="subtitle1" fontWeight="bold">{t('chatbox.guide.title')}</Typography>} 
              />
              <CardContent>
                <Typography variant="body2" paragraph>
                  {t('chatbox.guide.description')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('chatbox.guide.documentation')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </TabPanel>
  
        <TabPanel value={activeTab} index={1}>
        <Box sx={{display:'flex', flexDirection:'column', gap:'20px' ,alignItems:'center', padding: '4px'}}>

            <Box 
              sx={{
                display: 'flex',
                gap: 3,
                width: '100%',
                flexDirection: { xs: 'column', lg: 'row' }
              }}
            >
              {/* Settings Section */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: { lg: '400px' }
                }}
              >
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" mb={3} sx={{textAlign:'center', fontSize: { xs: '1rem', md: '1.25rem' }}}>
                    {t('chatbox.settings.title')}
                  </Typography>
                  
                  <Box sx={{display:'flex', gap:'10px', justifyContent:'space-around', alignContent:'center', flexWrap:'wrap'}}>


                    {/* Position Setting */}
                    <Box sx={{ 
                      flex: '1 1 calc(50% - 10px)',
                      minWidth: '250px', 
                      border: '2px solid #E0E0E0', 
                      borderRadius: '10px', 
                      padding: { xs: '15px', md: '30px' }, 
                      display:'flex', 
                      flexDirection:'column', 
                      gap:'10px' , 
                      alignContent:'center'
                    }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 , textAlign:'center', fontSize: { xs: '0.875rem', md: '1rem' }}}>
                        {t('chatbox.settings.position')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setPosition('right')}
                          sx={{
                            flex: 1,
                            paddingY:'5px',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            border: position === 'right' ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                            boxShadow: position === 'right' ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                            backgroundColor: position === 'right' ? '#4caf50' : 'transparent',
                            color: position === 'right' ? 'white' : 'inherit',
                            '&:hover': {
                              backgroundColor: position === 'right' ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                              border: '2px solid #4caf50',
                              boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                            }
                          }}
                        >
                          {t('chatbox.settings.right')}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setPosition('left')}
                          sx={{
                            flex: 1,
                            paddingY:'5px',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            border: position === 'left' ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                            boxShadow: position === 'left' ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                            backgroundColor: position === 'left' ? '#4caf50' : 'transparent',
                            color: position === 'left' ? 'white' : 'inherit',
                            '&:hover': {
                              backgroundColor: position === 'left' ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                              border: '2px solid #4caf50',
                              boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                            }
                          }}
                        >
                          {t('chatbox.settings.left')}
                        </Button>
                      </Box>
                    </Box>

                    {/* Default State Setting */}
                    <Box sx={{ 
                      flex: '1 1 calc(50% - 10px)',
                      minWidth: '250px',
                      border: '2px solid #E0E0E0', 
                      borderRadius: '10px', 
                      padding: { xs: '15px', md: '30px' }, 
                      display:'flex', 
                      flexDirection:'column', 
                      gap:'10px' , 
                      alignContent:'center'
                    }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 , textAlign:'center', fontSize: { xs: '0.875rem', md: '1rem' }}}>
                        {t('chatbox.settings.defaultState')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setDefaultState('closed')}
                          sx={{
                            flex: 1,
                            paddingY:'5px',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            border: defaultState === 'closed' ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                            boxShadow: defaultState === 'closed' ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                            backgroundColor: defaultState === 'closed' ? '#4caf50' : 'transparent',
                            color: defaultState === 'closed' ? 'white' : 'inherit',
                            '&:hover': {
                              backgroundColor: defaultState === 'closed' ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                              border: '2px solid #4caf50',
                              boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                            }
                          }}
                        >
                          {t('chatbox.settings.closed')}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setDefaultState('open')}
                          sx={{
                            flex: 1,
                            paddingY:'5px',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            border: defaultState === 'open' ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                            boxShadow: defaultState === 'open' ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                            backgroundColor: defaultState === 'open' ? '#4caf50' : 'transparent',
                            color: defaultState === 'open' ? 'white' : 'inherit',
                            '&:hover': {
                              backgroundColor: defaultState === 'open' ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                              border: '2px solid #4caf50',
                              boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                            }
                          }}
                        >
                          {t('chatbox.settings.open')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'space-around', alignContent: 'center', flexWrap: 'wrap', mt: 2, mb: 1 }}>
                    {/* Time Open Setting */}
                    <Box
                      sx={{
                        flex: '1 1 calc(50% - 10px)',
                        minWidth: '250px',
                        border: '2px solid #E0E0E0',
                        borderRadius: '10px',
                        padding: { xs: '15px', md: '30px' },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        {t('chatbox.settings.timeOpen.title')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          gap: { xs: 1, sm: 1 },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={timeOpenEnabled}
                              onChange={(event) => setTimeOpenEnabled(event.target.checked)}
                              color="primary"
                            />
                          }
                          label={timeOpenEnabled ? t('chatbox.settings.timeOpen.on') : t('chatbox.settings.timeOpen.off')}
                          sx={{ m: 0 }}
                        />
                        {timeOpenEnabled && (
                          <TextField
                            type="number"
                            value={timeOpenSeconds}
                            onChange={(event) => handleTimeOpenSecondsChange(event.target.value)}
                            onBlur={() => {
                              if (timeOpenSeconds < 1) setTimeOpenSeconds(1);
                            }}
                            inputProps={{ min: 1, step: 1 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {t('chatbox.settings.timeOpen.unit')}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Processing State Setting */}
                    <Box
                      sx={{
                        flex: '1 1 calc(50% - 10px)',
                        minWidth: '250px',
                        border: '2px solid #E0E0E0',
                        borderRadius: '10px',
                        padding: { xs: '15px', md: '30px' },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        {t('chatbox.settings.processing.title')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          gap: { xs: 1, sm: 10 },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={processingEnabled}
                              onChange={(event) => setProcessingEnabled(event.target.checked)}
                              color="primary"
                            />
                          }
                          label={processingEnabled ? t('chatbox.settings.processing.on') : t('chatbox.settings.processing.off')}
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* faq section */}
                  <Box
                    sx={{
                      mt: 1,
                      border: '2px solid #E0E0E0',
                      borderRadius: '10px',
                      p: { xs: 2, md: 3 },
                      '@media (max-width: 600px)': {
                        '& .faqCompactButton': {
                          width: 'fit-content !important',
                          minWidth: 'unset !important',
                          flex: '0 0 auto !important',
                          px: '10px !important',
                          py: '4px !important',
                          fontSize: '0.72rem !important',
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                        {t('chatbox.faq.title')}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleAddFaqQuestion}
                        disabled={faqQuestions.length >= 3}
                        className="faqCompactButton"
                        sx={{
                          width: 'auto',
                          minWidth: { xs: '92px', sm: '120px' },
                          px: { xs: 1.25, sm: 2 },
                          py: { xs: 0.5, sm: 0.75 },
                          fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                          flexShrink: 0,
                        }}
                      >
                        {t('chatbox.faq.addQuestion')}
                      </Button>
                    </Box>
                    <Typography variant="body2" paragraph>
                      {t('chatbox.faq.description')}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {t('chatbox.faq.description2')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {faqQuestions.map((question, index) => (
                        <Box key={`faq-question-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            label={`${t('chatbox.faq.question')} ${index + 1}`}
                            placeholder={t('chatbox.faq.placeholderQ')}
                            value={question}
                            onChange={(event) => handleFaqQuestionChange(index, event.target.value)}
                            multiline
                            minRows={1}
                            maxRows={6}
                            fullWidth
                          />
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleRemoveFaqQuestion(index)}
                            className="faqCompactButton"
                            sx={{
                              whiteSpace: 'nowrap',
                              width: 'auto',
                              minWidth: 'unset',
                              px: { xs: 1, sm: 1.5 },
                              py: { xs: 0.5, sm: 0.75 },
                              fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                              flexShrink: 0,
                            }}
                          >
                            {t('chatbox.faq.delete')}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Preview Section */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: { lg: '400px' }
                }}
              >
                <Paper sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" mb={{xs:1, md:3}} sx={{textAlign:'center', fontSize: { xs: '1rem', md: '1.25rem' }}}>
                    {t('chatbox.preview.title')}
                  </Typography>

                   {/* Preview Image */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px'
                    }}
                  >
                    <img 
                      src={
                        position === 'right' && defaultState === 'closed' ? '/images/RC.png' :
                        position === 'right' && defaultState === 'open' ? '/images/RO.png' :
                        position === 'left' && defaultState === 'closed' ? '/images/LC.png' :
                        '/images/LO.png'
                      }
                      alt="Chatbox Preview" 
                      style={{ border: '2px solid #E0E0E0',maxWidth: '90%', display: 'block' }}
                      onLoadStart={(e) => {
                        // Keep the previous image visible while loading new one
                        e.currentTarget.style.opacity = '0.3';
                      }}
                      onLoad={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Documentation Section */}
            <Box sx={{borderTop:'1px solid #E0E0E0', mt:4, px: {xs: 2 , md: 4} , width: '100%'}}>
                <Typography sx={{fontWeight:'bold', fontSize: { xs: '1rem', md: '1.2rem' }, mt: 4, mb: 2 }}>
                  {t('chatbox.documentation.integration.title')}
                </Typography>
                
                <Typography variant="body2" paragraph>
                  {t('chatbox.documentation.integration.description')}
                </Typography>
                
                <Box sx={{ position: 'relative', overflowX: 'auto', mb: 3 }}>
                  <Paper
                    sx={{
                      p: 0,
                      backgroundColor: 'grey.100',
                      position: 'relative',
                      overflowX: 'auto',
                    }}
                  >
                    <pre style={{ marginTop: 40, overflowWrap: 'break-word', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }} dir="ltr">
                      <code>
                        {scriptTagCode}
                      </code>
                    </pre>
                    <Tooltip title={copyTooltip}>
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(scriptTagCode);
                          setCopyTooltip(t('chatbox.message.copied'));
                          setTimeout(() => setCopyTooltip(t('chatbox.message.copy')), 2000);
                        }}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                </Box>

                <Typography sx={{fontWeight:'bold', fontSize: { xs: '1rem', md: '1.2rem' }, mt: 3, mb: 2 }}>
                  {t('chatbox.documentation.integration.prerequisites.title')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('chatbox.documentation.integration.prerequisites.description')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                  {tArray('chatbox.documentation.integration.prerequisites.list').map((item: string, index: number) => (
                    <Typography key={index} component="li" variant="body2" paragraph>
                      {item}
                    </Typography>
                  ))}
                </Box>

                <Typography sx={{fontWeight:'bold', fontSize: { xs: '1rem', md: '1.2rem' }, mt: 3, mb: 2 }}>
                  {t('chatbox.documentation.integration.wordpress.title')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('chatbox.documentation.integration.wordpress.description')}
                </Typography>

                <Typography sx={{fontWeight:'bold', fontSize: { xs: '0.9rem', md: '1.2rem' }, mt: 2, mb: 1 }}>
                  {t('chatbox.documentation.integration.wordpress.method1.title')}
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 3 }}>
                  {tArray('chatbox.documentation.integration.wordpress.method1.steps').map((step: string, index: number) => (
                    <Typography key={index} component="li" variant="body2" paragraph>
                      {step}
                    </Typography>
                  ))}
                </Box>

                <Typography sx={{fontWeight:'bold', fontSize: { xs: '0.9rem', md: '1.2rem' }, mt: 2, mb: 1 }}>
                  {t('chatbox.documentation.integration.wordpress.method2.title')}
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 3 }}>
                  {tArray('chatbox.documentation.integration.wordpress.method2.steps').map((step: string, index: number) => (
                    <Typography key={index} component="li" variant="body2" paragraph>
                      {step}
                    </Typography>
                  ))}
                </Box>

                <Typography sx={{fontWeight:'bold', fontSize: { xs: '0.9rem', md: '1.2rem' }, mt: 2, mb: 1 }}>
                  {t('chatbox.documentation.integration.wordpress.method3.title')}
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 3 }}>
                  {tArray('chatbox.documentation.integration.wordpress.method3.steps').map((step: string, index: number) => (
                    <Typography key={index} component="li" variant="body2" paragraph>
                      {step}
                    </Typography>
                  ))}
                </Box>

                <Typography sx={{fontWeight:'bold', fontSize: { xs: '0.9rem', md: '1.2rem' }, mt: 3, mb: 2 }}>
                  {t('chatbox.documentation.integration.testing.title')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('chatbox.documentation.integration.testing.description')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                  {tArray('chatbox.documentation.integration.testing.steps').map((step: string, index: number) => (
                    <Typography key={index} component="li" variant="body2" paragraph>
                      {step}
                    </Typography>
                  ))}
                </Box>
            </Box>
        </Box>
        </TabPanel>
      </Box>
    </Box>
  );
};

const Chatbox: FC = () => {
  const { steps_chatbox } = useTranslatedSteps();

  return (
    <TourProvider steps={steps_chatbox}>
      <VideoProvider videoUrl={videoUrls.chatBox} videoTitle={videoTitles.chatBox}>
        <ChatboxContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default Chatbox; 